import React from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  IconButton,
  Chip,
  LinearProgress,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  TrendingUp,
  Person,
  Home,
  CalendarToday,
  AttachMoney,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
  Notifications,
  Message,
  Assignment,
} from '@mui/icons-material';

// Mock data for charts and statistics
const statsData = [
  { title: 'Total Properties', value: '1,254', icon: <Home color="primary" />, change: '+12%', up: true },
  { title: 'Total Users', value: '3,872', icon: <Person color="secondary" />, change: '+8%', up: true },
  { title: 'Total Bookings', value: '942', icon: <CalendarToday color="success" />, change: '+15%', up: true },
  { title: 'Revenue', value: '$128,540', icon: <AttachMoney color="warning" />, change: '-3%', up: false },
];

const recentActivities = [
  { id: 1, user: 'John Doe', action: 'booked Property #1234', time: '10 minutes ago', avatar: 'J' },
  { id: 2, user: 'Sarah Smith', action: 'registered as a new user', time: '1 hour ago', avatar: 'S' },
  { id: 3, user: 'Mike Johnson', action: 'added a new property', time: '3 hours ago', avatar: 'M' },
  { id: 4, user: 'Emily Brown', action: 'submitted a support ticket', time: '5 hours ago', avatar: 'E' },
  { id: 5, user: 'David Wilson', action: 'canceled booking #5678', time: '1 day ago', avatar: 'D' },
];

const propertyTypes = [
  { name: 'Apartments', value: 45 },
  { name: 'Houses', value: 30 },
  { name: 'Condos', value: 15 },
  { name: 'Villas', value: 10 },
];

const monthlyBookings = [
  { month: 'Jan', value: 65 },
  { month: 'Feb', value: 59 },
  { month: 'Mar', value: 80 },
  { month: 'Apr', value: 81 },
  { month: 'May', value: 56 },
  { month: 'Jun', value: 55 },
  { month: 'Jul', value: 40 },
  { month: 'Aug', value: 70 },
  { month: 'Sep', value: 90 },
  { month: 'Oct', value: 110 },
  { month: 'Nov', value: 105 },
  { month: 'Dec', value: 120 },
];

const AdminDashboardPage: React.FC = () => {
  const theme = useTheme();

  return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Dashboard Overview
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DashboardIcon />}
              sx={{ mr: 2 }}
            >
              Generate Report
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} mb={4}>
          {statsData.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card elevation={3}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography color="textSecondary" variant="subtitle2" gutterBottom>
                        {stat.title}
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        {stat.value}
                      </Typography>
                      <Box display="flex" alignItems="center" mt={1}>
                        {stat.up ? (
                          <ArrowUpward fontSize="small" color="success" />
                        ) : (
                          <ArrowDownward fontSize="small" color="error" />
                        )}
                        <Typography
                          variant="body2"
                          color={stat.up ? 'success.main' : 'error.main'}
                          ml={0.5}
                        >
                          {stat.change}
                        </Typography>
                      </Box>
                    </Box>
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.background.default,
                        width: 56,
                        height: 56,
                      }}
                    >
                      {stat.icon}
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={4}>
          {/* Chart: Monthly Bookings */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                height: 360,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" component="h2">
                  Monthly Bookings
                </Typography>
                <IconButton size="small">
                  <MoreVert />
                </IconButton>
              </Box>
              
              {/* Mock Bar Chart */}
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'flex-end', height: '250px' }}>
                {monthlyBookings.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      height: `${(item.value / 120) * 100}%`,
                      width: '7%',
                      mx: '0.5%',
                      bgcolor: theme.palette.primary.main,
                      borderRadius: '4px 4px 0 0',
                      position: 'relative',
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        bottom: '-25px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                      }}
                    >
                      {item.month}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Property Types */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                height: 360,
              }}
            >
              <Typography variant="h6" component="h2" gutterBottom>
                Property Types
              </Typography>
              
              {/* Mock Pie Chart as progress bars */}
              <Box sx={{ mt: 3, flexGrow: 1 }}>
                {propertyTypes.map((type, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">{type.name}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {type.value}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={type.value}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: theme.palette.background.default,
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 5,
                          bgcolor: index === 0
                            ? theme.palette.primary.main
                            : index === 1
                            ? theme.palette.secondary.main
                            : index === 2
                            ? theme.palette.success.main
                            : theme.palette.warning.main,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>

              <Box mt="auto">
                <Button variant="outlined" fullWidth>
                  View All Properties
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Recent Activities */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 0, overflow: 'hidden' }}>
              <CardHeader
                title="Recent Activities"
                action={
                  <IconButton aria-label="settings">
                    <MoreVert />
                  </IconButton>
                }
                sx={{ px: 3, py: 2 }}
              />
              <Divider />
              <List sx={{ px: 0 }}>
                {recentActivities.map((activity, index) => (
                  <React.Fragment key={activity.id}>
                    <ListItem alignItems="flex-start" sx={{ px: 3, py: 2 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                          {activity.avatar}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" component="span">
                            {activity.user}
                          </Typography>
                        }
                        secondary={
                          <React.Fragment>
                            <Typography
                              component="span"
                              variant="body2"
                              color="textPrimary"
                            >
                              {activity.action}
                            </Typography>
                            <Typography
                              component="div"
                              variant="caption"
                              color="textSecondary"
                              sx={{ mt: 0.5 }}
                            >
                              {activity.time}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                    {index < recentActivities.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
              <Divider />
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Button size="small" color="primary">
                  View All Activities
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Tasks & Notifications */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" component="h2">
                  Tasks & Notifications
                </Typography>
                <Box>
                  <Chip
                    icon={<Notifications fontSize="small" />}
                    label="5 new"
                    size="small"
                    color="primary"
                    sx={{ mr: 1 }}
                  />
                  <IconButton size="small">
                    <MoreVert />
                  </IconButton>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <List sx={{ p: 0 }}>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: theme.palette.success.light }}>
                      <Assignment />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Review new property listings"
                    secondary="Due today"
                  />
                  <Chip label="High" size="small" color="error" />
                </ListItem>
                <Divider variant="inset" component="li" />
                
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: theme.palette.warning.light }}>
                      <Message />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Respond to customer inquiries"
                    secondary="3 pending messages"
                  />
                  <Chip label="Medium" size="small" color="warning" />
                </ListItem>
                <Divider variant="inset" component="li" />
                
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: theme.palette.info.light }}>
                      <TrendingUp />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Update monthly financial report"
                    secondary="Due in 2 days"
                  />
                  <Chip label="Low" size="small" color="info" />
                </ListItem>
              </List>
              
              <Box mt={2}>
                <Button variant="outlined" fullWidth startIcon={<Assignment />}>
                  View All Tasks
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
  );
};

export default AdminDashboardPage;